import React, { useEffect, useState } from 'react';
import Shell from '../../components/Shell';
import EcosystemCharts, {
  RestructuringChart,
} from '../../components/EcosystemCharts';
import {
  RestructuringStoryCore,
  StoryBlock,
} from '../../components/RestructuringStory';
import api from '../../services/api';

type Company = {
  id: string;
  name: string;
  kind: string;
  status: string;
  ownership: string;
  role: string;
};

type EquityAsset = {
  id: string;
  label: string;
  value: string;
};

type PartnerSeatsBlock = {
  title: string;
  summary: string;
  valuation: string;
  raise: string;
  ownershipOffered: string;
  minimum: string;
  assetsTitle?: string;
  assets?: EquityAsset[];
  assetsTotal?: string;
  note: string;
};

type IntroBlock = {
  headline: string;
  bodyHtml: string;
};

type HoldingsRow = {
  id: string;
  item: string;
  ownership: string;
  assetValue: string;
  debt: string;
  net: string;
};

type IntendedHoldingsRow = {
  id: string;
  item: string;
  net: string;
  reason: string;
};

type IntendedHoldingsGroup = {
  id: string;
  destination: string;
  summary: string;
  net: string;
  items: IntendedHoldingsRow[];
};

type HoldingsBlock = {
  currentTitle: string;
  currentNote: string;
  current: HoldingsRow[];
  currentTotals: {
    assetValue: string;
    debt: string;
    net: string;
  };
  intendedTitle: string;
  intendedNote: string;
  intendedGroups: IntendedHoldingsGroup[];
};

type PagePayload = {
  slug: string;
  title: string;
  status: string;
  subtitle: string;
  disclaimer: string;
  intro?: IntroBlock | null;
  story?: StoryBlock | null;
  partnerSeats?: PartnerSeatsBlock | null;
  holdings?: HoldingsBlock | null;
  companies: Company[];
  charts: RestructuringChart[];
  bodyHtml: string;
};

const kindOrder = [
  'Consular / Sponsor',
  'Operating company',
  'Legacy / runoff',
  'General Partner',
  'Fund',
  'Independent company',
  'IP / spinout track',
];

export default function RestructuringPage() {
  const [page, setPage] = useState<PagePayload | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get('/portal/restructuring')
      .then((r) => setPage(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) {
          setError('You do not have access to the restructuring materials.');
        } else {
          setError('Restructuring page unavailable.');
        }
      });
  }, []);

  const grouped = kindOrder
    .map((kind) => ({
      kind,
      items: (page?.companies || []).filter((c) => c.kind === kind),
    }))
    .filter((g) => g.items.length > 0);

  const leftover = (page?.companies || []).filter(
    (c) => !kindOrder.includes(c.kind),
  );
  if (leftover.length) {
    grouped.push({ kind: 'Other', items: leftover });
  }

  const equity = page?.partnerSeats;
  const holdings = page?.holdings;

  return (
    <Shell title="Restructuring">
      {error && <p className="text-red-700">{error}</p>}
      {page && (
        <div className="animate-fade-up">
          <p className="text-xs uppercase tracking-[0.22em] text-navy-700">
            Target structure
            <span className="mx-2 text-mist-200">·</span>
            {page.status}
          </p>
          <h1 className="mt-2 font-display text-3xl text-ink-900 md:text-4xl">
            {page.title}
          </h1>
          {page.subtitle && (
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-ink-700">
              {page.subtitle}
            </p>
          )}
          {page.disclaimer && (
            <p className="mt-4 max-w-3xl border-l-2 border-navy-400 pl-4 text-sm text-ink-700">
              {page.disclaimer}
            </p>
          )}

          {page.intro && (
            <section className="mt-10 max-w-3xl">
              <h2 className="font-display text-2xl text-ink-900">
                {page.intro.headline}
              </h2>
              <div
                className="prose-fund mt-4"
                dangerouslySetInnerHTML={{ __html: page.intro.bodyHtml }}
              />
            </section>
          )}

          {page.story && <RestructuringStoryCore story={page.story} />}

          {equity && (
            <section className="mt-12 border border-mist-200 bg-gradient-to-br from-white via-mist-50 to-mist-100/80 px-5 py-6 md:px-8 md:py-8">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-w-2xl">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-700">
                    Consular equity
                  </p>
                  <h2 className="mt-2 font-display text-2xl text-ink-900">
                    {equity.title}
                  </h2>
                  {equity.summary && (
                    <p className="mt-2 text-sm leading-relaxed text-ink-700">
                      {equity.summary}
                    </p>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-ink-700">
                      Valuation
                    </dt>
                    <dd className="mt-0.5 font-medium text-ink-900">
                      {equity.valuation}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-ink-700">
                      Raise
                    </dt>
                    <dd className="mt-0.5 font-medium text-ink-900">
                      {equity.raise}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-ink-700">
                      Ownership
                    </dt>
                    <dd className="mt-0.5 font-medium text-ink-900">
                      {equity.ownershipOffered}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-ink-700">
                      Minimum
                    </dt>
                    <dd className="mt-0.5 font-medium text-navy-700">
                      {equity.minimum}
                    </dd>
                  </div>
                </dl>
              </div>
              {equity.assets && equity.assets.length > 0 && (
                <div className="mt-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-700">
                    {equity.assetsTitle || 'Supporting assets'}
                  </p>
                  <ul className="mt-4 divide-y divide-mist-200 border-y border-mist-200">
                    {equity.assets.map((asset) => (
                      <li
                        key={asset.id}
                        className="flex items-baseline justify-between gap-6 py-3"
                      >
                        <span className="font-display text-base text-ink-900">
                          {asset.label}
                        </span>
                        <span className="shrink-0 text-sm font-medium text-ink-900">
                          {asset.value}
                        </span>
                      </li>
                    ))}
                    {equity.assetsTotal && (
                      <li className="flex items-baseline justify-between gap-6 py-3">
                        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-700">
                          Total
                        </span>
                        <span className="shrink-0 text-sm font-semibold text-navy-700">
                          {equity.assetsTotal}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {equity.note && (
                <p className="mt-5 max-w-3xl text-xs leading-relaxed text-ink-700">
                  {equity.note}
                </p>
              )}
            </section>
          )}

          {holdings && (
            <section className="mt-12">
              <h2 className="font-display text-2xl text-ink-900">
                Holdings and destination
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-700">
                The catch-all ledger versus where each line is intended to live
                after Inc. formation, the 80/20 stock round, and contribution
                without extra Consular shares.
              </p>

              {holdings.current.length > 0 && (
                <div className="mt-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-700">
                    {holdings.currentTitle}
                  </p>
                  {holdings.currentNote && (
                    <p className="mt-2 max-w-3xl text-sm text-ink-700">
                      {holdings.currentNote}
                    </p>
                  )}
                  <div className="mt-4 overflow-x-auto border border-mist-200 bg-white">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-mist-200 bg-mist-50 text-xs uppercase tracking-wide text-ink-700">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Item</th>
                          <th className="px-3 py-2 font-semibold">Ownership</th>
                          <th className="px-3 py-2 font-semibold text-right">
                            Asset value
                          </th>
                          <th className="px-3 py-2 font-semibold text-right">
                            Debt
                          </th>
                          <th className="px-3 py-2 font-semibold text-right">
                            Net
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-mist-200">
                        {holdings.current.map((row) => (
                          <tr key={row.id}>
                            <td className="px-3 py-2 text-ink-900">
                              {row.item}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                              {row.ownership}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-ink-900">
                              {row.assetValue || '—'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-ink-900">
                              {row.debt || '—'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-ink-900">
                              {row.net || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {(holdings.currentTotals.assetValue ||
                        holdings.currentTotals.debt ||
                        holdings.currentTotals.net) && (
                        <tfoot className="border-t border-mist-200 bg-mist-50 text-sm font-semibold text-navy-700">
                          <tr>
                            <td className="px-3 py-2" colSpan={2}>
                              Totals (ownership-adjusted net)
                            </td>
                            <td className="px-3 py-2 text-right">
                              {holdings.currentTotals.assetValue}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {holdings.currentTotals.debt}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {holdings.currentTotals.net}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {holdings.intendedGroups.length > 0 && (
                <div className="mt-10">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-700">
                    {holdings.intendedTitle}
                  </p>
                  {holdings.intendedNote && (
                    <p className="mt-2 max-w-3xl text-sm text-ink-700">
                      {holdings.intendedNote}
                    </p>
                  )}
                  <div className="mt-6 space-y-6">
                    {holdings.intendedGroups.map((group) => (
                      <div
                        key={group.id}
                        className="border border-mist-200 bg-white"
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-mist-200 bg-mist-50 px-4 py-3">
                          <h3 className="font-display text-base text-ink-900">
                            {group.destination}
                          </h3>
                          {group.net && (
                            <p className="text-xs uppercase tracking-wide text-navy-700">
                              Net {group.net}
                            </p>
                          )}
                        </div>
                        {group.summary && (
                          <p className="px-4 pt-3 text-sm leading-relaxed text-ink-700">
                            {group.summary}
                          </p>
                        )}
                        <ul className="divide-y divide-mist-200 px-4 py-3">
                          {group.items.map((row) => (
                            <li key={row.id} className="py-3">
                              <div className="flex flex-wrap items-baseline justify-between gap-3">
                                <span className="text-sm font-medium text-ink-900">
                                  {row.item}
                                </span>
                                {row.net && (
                                  <span className="shrink-0 text-sm text-ink-900">
                                    {row.net}
                                  </span>
                                )}
                              </div>
                              {row.reason && (
                                <p className="mt-1 text-sm text-ink-700">
                                  {row.reason}
                                </p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          <EcosystemCharts charts={page.charts || []} />

          <section className="mt-14">
            <h2 className="font-display text-2xl text-ink-900">
              Companies & structures
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-ink-700">
              Entity roster for the target ecosystem—roles, ownership, and
              formation status.
            </p>
            <div className="mt-8 space-y-10">
              {grouped.map((group) => (
                <div key={group.kind}>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-700">
                    {group.kind}
                  </h3>
                  <ul className="mt-4 grid gap-4 md:grid-cols-2">
                    {group.items.map((company) => (
                      <li
                        key={company.id}
                        className="border border-mist-200 bg-white px-5 py-4"
                      >
                        <p className="font-medium text-ink-900">
                          {company.name}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-wide text-ink-700">
                          {company.status}
                        </p>
                        {company.ownership && (
                          <p className="mt-3 text-sm text-ink-700">
                            <span className="font-medium text-ink-900">
                              Ownership.
                            </span>{' '}
                            {company.ownership}
                          </p>
                        )}
                        {company.role && (
                          <p className="mt-2 text-sm leading-relaxed text-ink-700">
                            <span className="font-medium text-ink-900">
                              Role.
                            </span>{' '}
                            {company.role}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {page.bodyHtml && (
            <div
              className="prose-fund mt-14 max-w-3xl"
              dangerouslySetInnerHTML={{ __html: page.bodyHtml }}
            />
          )}
        </div>
      )}
    </Shell>
  );
}
